import {
  convertEffort,
  effortLabel,
  isEffortDerivedWeightSource,
  type EffortScale,
  type WeightSource,
} from '@heraion/shared';

// PG-9 — the sentence that turns a derived weight into an honest PROPOSAL.
//
// A set on the effort axis reaches the logger with a load nobody wrote down:
// the app worked it back from the athlete's own numbers so the set lands on
// the prescribed effort. §17 of the measurement contract calls that a
// proposal, and a proposal has to say so and say WHICH instrument answered —
// otherwise a load derived from a single they really attempted and one
// derived from an old training set wear the same face.
//
// Pure and outside the component for two reasons: FocusPanelInputs was at 498
// lines with this inline (the cap is 500), and the branch that decides
// whether an athlete sees an explanation at all deserves a test of its own
// rather than a mount.
export const weightProposalTip = (opts: {
  weightSource: WeightSource | undefined;
  /** The prescribed effort on this set, in canonical RIR (DraftSet.rirPrescribed). */
  rirPrescribed: number | undefined;
  /** The scale the athlete reads effort in. A target spelled "RIR 2" to
   *  someone who logs in RPE states the same effort with the opposite
   *  number, which is the whole reason effortScale.ts exists. */
  effortScale: EffortScale;
  /** The history weight is a fallback (no e1RM from the last set). */
  fellBack?: boolean;
  t: (key: string, opts: Record<string, unknown>) => string;
}): string | undefined => {
  const { weightSource, rirPrescribed, effortScale, fellBack, t } = opts;
  if (fellBack === true && weightSource === 'history') {
    if (rirPrescribed === undefined) return undefined;
    return t('mobile.logger.proposedWeightTipHistoryFallback', {
      defaultValue:
        'Last weight used: your last set cannot give an estimate of your max. A starting point, not a prescription: change it.',
    });
  }
  if (!isEffortDerivedWeightSource(weightSource)) return undefined;
  // ⛔ No target, no sentence. The cue exists to say "proposed so you land on
  // RIR 2"; with nothing to land on there is nothing to explain, and a mute
  // glyph beside the label would be worse than none. The weight still shows,
  // exactly as it always has.
  if (rirPrescribed === undefined) return undefined;
  const effort = effortLabel(
    convertEffort(rirPrescribed, 'rir', effortScale),
    effortScale,
  );
  if (weightSource === 'effort-from-tested-max') {
    return t('mobile.logger.proposedWeightTipTestedMax', {
      effort,
      defaultValue:
        'Proposed so this set lands on {{effort}}, worked back from the 1RM you tested. A starting point, not a prescription: change it.',
    });
  }
  if (weightSource === 'effort-from-max') {
    return t('mobile.logger.proposedWeightTipMax', {
      effort,
      defaultValue:
        'Proposed so this set lands on {{effort}}, worked back from your recorded max. A starting point, not a prescription: change it.',
    });
  }
  return t('mobile.logger.proposedWeightTipHistory', {
    effort,
    defaultValue:
      'Proposed so this set lands on {{effort}}, worked back from your last set on this exercise. A starting point, not a prescription: change it.',
  });
};
